import * as STD from '@emersya/mpa-std';

abstract class Condition {
	static deserialize(
		serialized: Condition.TSerialized,
		generateId?: () => number
	) {
		if (!generateId) {
			let baseId = 0;
			generateId = () => baseId++;
		}
		let condition;
		if (Condition.prototype.isScalar.call(serialized)) {
			condition =
				new Condition.ScalarCondition(<Condition.ScalarCondition.ISerialized>serialized, generateId);
		} else {
			condition =
				new Condition.AggregateCondition(
					<Condition.AggregateCondition.ISerialized>serialized,
					generateId
				);
		}
		return condition;
	};

	static walk<TCondition extends Condition.TSerialized | Condition.TCondition>(condition: TCondition, func: (condition: TCondition) => void) {
		const stack: TCondition[] = [condition];

		while (stack.length > 0) {
			const condition = stack.shift()!;

			func(condition);

			if ('LHS' in condition)
				stack.push(<TCondition>condition.LHS);

			if ('RHS' in condition)
				stack.push(<TCondition>condition.RHS);
		}
	}

	readonly id: number;
	readonly abstract type: string;
	errorId?: string;

	async evaluateToReport(
		hostComponent: Component<any, any>
	) {
		const reportItems: Condition.IEvaluationReportItem[] = [];

		const result = await this.evaluate(hostComponent, reportItems);

		return new Condition.EvaluationReport(result, reportItems);
	}

	isScalar(): this is Condition.ScalarCondition {
		return [
			'equal', 'notequal',
			'inferior', 'inferiorequal',
			'superior', 'superiorequal',
		].includes(this.type);
	};

	abstract evaluate(
		hostComponent: Component<any, any>,
		report: Condition.IEvaluationReportItem[]
	): Promise<boolean>;

	protected constructor(generateId: () => number) {
		this.id = generateId();
	};
};
namespace Condition {
	export interface ISerialized {
		readonly type: string;
		errorId?: string;
	};

	export interface IEvaluationReportItem {
		condition: TCondition;
		result: boolean;
		component?: Component<any, any> | null;
		property?: any;
	};

	export class EvaluationReport {
		result: boolean;
		items: IEvaluationReportItem[];

		getErrorId() {
			if (this.result) {
				throw STD.errors.user('Cannot get errorId if condition is true', {report: this});
			}
			for (let i = this.items.length - 1; i >= 0; i--) {
				if (this.items[i]?.condition.errorId) {
					return this.items[i]!.condition.errorId;
				}
			}
			return null;
		};

		constructor(result: boolean, items: IEvaluationReportItem[]) {
			this.result = result;
			this.items = items;
		};
	};

	export class AggregateCondition extends Condition {
		override readonly type: 'or' | 'and';
		readonly LHS: Condition;
		readonly RHS: Condition;

		async evaluate(
			hostComponent: Component<any, any>,
			reportItems: Condition.IEvaluationReportItem[] = []
		): Promise<boolean> {
			try {
				let result = false;
				result = (
					(this.type === 'and') ? (await this.LHS.evaluate(hostComponent, reportItems) && await this.RHS.evaluate(hostComponent, reportItems)) :
						(this.type === 'or') ? (await this.LHS.evaluate(hostComponent, reportItems) || await this.RHS.evaluate(hostComponent, reportItems)) :
							false
				);
				reportItems.push({
					condition: this,
					result
				});
				return result;
			} catch (error: any) {
				throw STD.errors.bindAsBound(
					'Cannot evaluate condition!',
					{
						condition: this,
						report: reportItems,
						hostComponent
					},
					error
				);
			}
		};

		constructor(
			serialized: AggregateCondition.ISerialized,
			generateId: () => number
		) {
			super(generateId);
			this.type = serialized.type;
			this.LHS = Condition.deserialize(serialized.LHS, generateId);
			this.RHS = Condition.deserialize(serialized.RHS, generateId);
			this.errorId = serialized.errorId;
		};
	};
	export namespace AggregateCondition {
		export interface ISerialized extends Condition.ISerialized {
			readonly type: AggregateCondition['type'];
			readonly LHS: TSerialized;
			readonly RHS: TSerialized;
		};
	}
	;

	export class ScalarCondition extends Condition {
		static types = ['equal', 'notequal',
			'inferior', 'inferiorequal',
			'superior', 'superiorequal'] as const;

		static isSerializedScalar(condition: TSerialized): condition is ScalarCondition.ISerialized {
			return (<string[]><unknown>Condition.ScalarCondition.types).includes(condition.type);
		}

		readonly type: typeof ScalarCondition.types[number];
		readonly component: Path.TPathRaw;
		readonly property: STD.object.TPropertyPath;
		readonly value: STD.object.JSON;

		async evaluate(
			hostComponent: Component<any, any>,
			report: Condition.IEvaluationReportItem[] = []
		): Promise<boolean> {
			try {
				let result = false;
				let property: any;
				let component: Component<any, any> | null | undefined;
				component = hostComponent.component_find(
					Path.parse(this.component)
				);
				if (!component) {
					console.warn('Component not found', this);
					component = null;
				} else {
					property = await this.property.evaluate(Manager.generatePropertyEvaluateVariables(
						hostComponent,
						this,
						component
					));
					if ((this.type === 'equal') || (this.type === 'notequal')) {
						result = (
							(this.type === 'equal') ? (property === this.value) :
								(property !== this.value)
						);
					} else if (
						(this.type === 'inferior') || (this.type === 'inferiorequal') ||
						(this.type === 'superior') || (this.type === 'superiorequal')
					) {
						const v = (typeof this.value === 'number') ? this.value : <number>{};
						result = (
							(this.type === 'inferior') ? (property < v) :
								(this.type === 'inferiorequal') ? (property <= v) :
									(this.type === 'superior') ? (property > v) :
										(property >= v)
						);
					}
				}

				report.push({
					condition: this,
					result,
					component,
					property
				});
				return result;
			} catch (error: any) {
				throw STD.errors.bindAsBound(
					'Cannot evaluate condition!',
					{
						condition: this,
						report,
						hostComponent
					},
					error
				);
			}
		};

		constructor(
			serialized: ScalarCondition.ISerialized,
			generateId: () => number
		) {
			super(generateId);
			this.type = serialized.type;
			this.value = serialized.value;
			this.component = serialized.component;
			this.property = STD.object.PropertyPath.create(serialized.property);
			this.errorId = serialized.errorId;
		};
	};
	export namespace ScalarCondition {
		export interface ISerialized extends Condition.ISerialized {
			readonly type: ScalarCondition['type'];
			readonly component: Path.TPathRaw;
			readonly property: string;
			readonly value: STD.object.JSON;
		};
	}

	export type TCondition = AggregateCondition | ScalarCondition;
	export type TSerialized = AggregateCondition.ISerialized | ScalarCondition.ISerialized;
}
export default Condition;
import Component from './Component';
import Path from './Path';
import Manager from './Manager';
